import { DetailedSaleItem, ProductPackage } from '@src/core/buyerApiClient';
import useTranslate from '@src/i18n/useTranslate';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import React, { useMemo } from 'react';

import { CustomAccordion } from '@planne-software/uni/Accordion';
import SalePackageItemDetail from './SalePackageItemDetail';
import {
  PackageDates,
  PackageDiscountValue,
  PackageOriginalPrice,
  PackagePriceInfo,
  PackagePrices,
  PackageProductDetails,
  PackageProductDetailsContent,
  PackageProductWrapper,
  PackageTariffInfo,
  PackageTotalPrice,
  Product,
  ProductName,
} from './styles';
import { calculatePackageTotalCurrencyInfo } from '@src/domains/cart';
import { getFormattedTariffs } from '@src/features/orders/core/getFormattedTariffs';
import { getPackageDates } from '@src/features/orders/core/getPackageDates';
import { getDiscountCurrencyInfo } from '@src/domains/coupons';

interface PackageItemGroupProps {
  productPackage: ProductPackage;
  items: DetailedSaleItem[];
  showPlaceholderImage?: boolean;
}

const ProductPackageGroup = (props: PackageItemGroupProps) => {
  const { productPackage, items, showPlaceholderImage = false } = props;

  const { i18n, locale } = useTranslate();
  const { formatCurrencyInfo } = useCurrencyFormat();

  const packageDates = useMemo(() => getPackageDates(items, locale), [items, locale]);
  const formattedTariffs = useMemo(() => getFormattedTariffs(items), [items]);

  const fullPriceCurrencyInfo = useMemo(() => {
    return calculatePackageTotalCurrencyInfo(items, (item) => item.fullPriceCurrencyInfo);
  }, [items]);

  const totalPriceCurrencyInfo = useMemo(() => {
    return calculatePackageTotalCurrencyInfo(items, (item) => item.totalPriceCurrencyInfo);
  }, [items]);

  const discountCurrencyInfo = useMemo(() => {
    return getDiscountCurrencyInfo(fullPriceCurrencyInfo, totalPriceCurrencyInfo);
  }, [fullPriceCurrencyInfo, totalPriceCurrencyInfo]);

  return (
    <Product key={`${productPackage.name}-${productPackage.id}`}>
      <ProductName>{productPackage.name}</ProductName>

      {packageDates && (
        <PackageDates>
          {i18n('cart_package_date')} {packageDates}
        </PackageDates>
      )}

      <PackageTariffInfo>
        {i18n('cart_package_tariff')} {formattedTariffs}
      </PackageTariffInfo>

      <PackagePrices>
        <PackagePriceInfo>
          {i18n('cart_package_previous_price')}
          <PackageOriginalPrice>{formatCurrencyInfo(fullPriceCurrencyInfo)}</PackageOriginalPrice>
        </PackagePriceInfo>

        <PackagePriceInfo>
          {i18n('cart_package_discount')}
          <PackageDiscountValue>-{formatCurrencyInfo(discountCurrencyInfo)}</PackageDiscountValue>
        </PackagePriceInfo>

        <PackagePriceInfo>
          {i18n('cart_package_total')}
          <PackageTotalPrice>{formatCurrencyInfo(totalPriceCurrencyInfo)}</PackageTotalPrice>
        </PackagePriceInfo>
      </PackagePrices>

      <CustomAccordion
        title={<PackageProductDetails>{i18n('cart_package_product_details')}</PackageProductDetails>}
        showBorderBottom
        content={
          <PackageProductDetailsContent>
            <PackageProductWrapper>
              {items.map((item) => (
                <SalePackageItemDetail showPlaceholderImage={showPlaceholderImage} key={item.id} item={item} />
              ))}
            </PackageProductWrapper>
          </PackageProductDetailsContent>
        }
      />
    </Product>
  );
};

export default ProductPackageGroup;
